import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';

/**
 * [Kullanım Kılavuzu] Admin'in yayınladığı sistem ipucu/duyuru nudge'larını gösterir.
 * Sayfaların en üstüne ELLE monte edilir: <SystemNudgeCarousel pageKey="dashboard" />
 * Mavi tema. Tek nudge → düz kart; birden fazla → ileri/geri carousel + noktalar.
 * Yayınlı nudge yoksa hiçbir şey render etmez (null).
 */
export default function SystemNudgeCarousel({ pageKey, className = '' }) {
  const navigate = useNavigate();
  const [nudges, setNudges] = useState([]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!pageKey) return undefined;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/system-nudges`, { params: { pageKey } });
        if (!cancelled && res.data?.success) {
          setNudges(Array.isArray(res.data.nudges) ? res.data.nudges : []);
          setIndex(0);
        }
      } catch (_e) {
        if (!cancelled) setNudges([]);
      }
    })();
    return () => { cancelled = true; };
  }, [pageKey]);

  if (!nudges.length) return null;

  const total = nudges.length;
  const safeIndex = Math.min(index, total - 1);
  const current = nudges[safeIndex];
  const go = (dir) => setIndex((i) => (i + dir + total) % total);
  const hasCta = Boolean(current.ctaPageKey);

  return (
    <div className={`rounded-2xl border border-sky-200 bg-sky-50/80 px-5 py-4 dark:border-sky-900/40 dark:bg-sky-950/20 ${className}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
        <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-sky-500/15 text-sky-600 dark:text-sky-300 sm:mt-0">
          <span className="material-symbols-outlined text-[20px]">lightbulb</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-extrabold text-sky-900 dark:text-sky-100">{current.title}</p>
            {total > 1 && (
              <div className="flex shrink-0 items-center gap-1.5">
                <button
                  type="button"
                  aria-label="Önceki"
                  onClick={() => go(-1)}
                  className="flex size-7 items-center justify-center rounded-lg border border-sky-200 bg-white text-sky-700 transition hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-900/30 dark:text-sky-200"
                >
                  <span className="material-symbols-outlined text-[16px]">chevron_left</span>
                </button>
                <span className="text-[11px] font-bold text-sky-700 dark:text-sky-300">{safeIndex + 1}/{total}</span>
                <button
                  type="button"
                  aria-label="Sonraki"
                  onClick={() => go(1)}
                  className="flex size-7 items-center justify-center rounded-lg border border-sky-200 bg-white text-sky-700 transition hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-900/30 dark:text-sky-200"
                >
                  <span className="material-symbols-outlined text-[16px]">chevron_right</span>
                </button>
              </div>
            )}
          </div>
          <p className="mt-1 whitespace-pre-line text-xs leading-relaxed text-sky-800/90 dark:text-sky-200/80">{current.body}</p>
          {total > 1 && (
            <div className="mt-2.5 flex items-center gap-1.5">
              {nudges.map((n, i) => (
                <button
                  key={n.id}
                  type="button"
                  aria-label={`Nudge ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className={`h-1.5 rounded-full transition-all ${i === safeIndex ? 'w-5 bg-sky-500' : 'w-1.5 bg-sky-300 dark:bg-sky-700'}`}
                />
              ))}
            </div>
          )}
        </div>
        </div>
        {hasCta && (
          <button
            type="button"
            onClick={() => navigate(`/${current.ctaPageKey}`)}
            className="inline-flex w-full shrink-0 items-center justify-center gap-1.5 rounded-xl bg-sky-600 px-3.5 py-2 text-xs font-bold text-white shadow-sm transition-all hover:brightness-110 sm:w-auto"
          >
            {current.ctaLabel || 'Git'}
            <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
          </button>
        )}
      </div>
    </div>
  );
}
